import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ENV } from '@environment';
import { FormikHelpers } from 'formik';
import moment from 'moment';
import { RootState } from 'store';

import { fetchCancellationPolicies } from 'actions/cancellationPolicies';
import CancellationPolicies from 'components/CancellationPolicies';
import BreakTag from 'components/Tags/BreakTag';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { CancellationPolicies as CancellationPoliciesModel } from 'models/cancellationPolicies';
import { HotelDetailsCompleteResponse, Room } from 'models/hotelDetailsComplete';
import { AddToCartEvent, amplitudelogEvent } from 'services/amplitudeService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { getFormattedPrice } from 'utils/currency';
import { daysCount, extractErrorMessage } from 'utils/generic';

import {
  BestOptionLabel,
  NonRefundableIcon,
  RefundableIcon,
  PerNightValue,
  TextIsRefundable,
  TextIsNotRefundable,
  Value,
  RoomTitle,
} from '../../styles';
import { Container, Fare, Description, Selection, Footer, Tag, ContentRefundableItems } from './styles';

interface Props {
  rooms: Room[];
  visibility: () => void;
  checkinDate: Date;
  checkoutDate: Date;
  currencyCode: string;
  name?: string;
  searchId: string;
  optionId: string;
  bookingDisabled: boolean;
  details: HotelDetailsCompleteResponse;
  formik?: FormikHelpers<{
    roomRateId: string;
    roomRate: { refundableType: string };
  }>;
}

const ROOM_INDEX_BEST_OPTION = 0;

const Rooms = ({
  rooms,
  visibility,
  checkinDate,
  checkoutDate,
  currencyCode,
  name,
  formik,
  searchId,
  optionId,
  bookingDisabled,
  details,
}: Props) => {
  const toast = useToast();
  const { t } = useStoreTranslation();
  const { accountResult } = useSelector((state: RootState) => state.user);
  const [policies, setPolicies] = useState<Record<string, CancellationPoliciesModel[]>>({});
  const [isFetchingPolicies, setIsFetchingPolicies] = useState<Record<string, boolean>>({});
  const [showPolicies, setShowPolicies] = useState<Record<string, boolean>>({});

  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency: currencyCode, t });
    return formatted;
  };

  const handleTogglePolicies = async (rateId: string) => {
    if (!accountResult || isFetchingPolicies[rateId]) return;

    if (showPolicies[rateId]) {
      setShowPolicies((p) => ({ ...p, [rateId]: false }));
      return;
    }

    if (policies[rateId]) {
      setShowPolicies((p) => ({ ...p, [rateId]: true }));
      return;
    }

    try {
      setIsFetchingPolicies((p) => ({ ...p, [rateId]: true }));
      setShowPolicies((p) => ({ ...p, [rateId]: true }));

      const res = await fetchCancellationPolicies({
        sessionToken: accountResult.sessionToken,
        rateId,
        optionId,
        searchId,
      });
      setPolicies((p) => ({ ...p, [rateId]: res.data.data.cancellationPolicies }));
      setIsFetchingPolicies((p) => ({ ...p, [rateId]: false }));
    } catch (error) {
      setShowPolicies((p) => ({ ...p, [rateId]: false }));
      toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
    }
  };

  const sendAmplitudeEvent = (room: Room, roomIndex: number) => {
    const amplitudeEvent: AddToCartEvent = {
      INDEX: roomIndex,
      SOURCE: 'LIST',
      TRAVEL_PRODUCT_TYPE: 'HOTELS',
      TRAVEL_FROM: '',
      TRAVEL_TO: details?.cityCode ?? '',
      TRAVEL_DESTINATION_TYPE: '',
      TRAVEL_TRIP_TYPE: '',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), checkinDate).toString(),
      TRAVEL_DATE_FROM: checkinDate?.toString() ?? '',
      TRAVEL_DATE_TO: checkoutDate?.toString() ?? '',
      TRAVEL_TRIP_LENGTH: daysCount(checkinDate, checkoutDate).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_ADULTS: '1',
      TRAVEL_CHILDREN: '0',
      TRAVEL_INFANTS: '0',
      TRAVEL_CLASS_OF_SERVICE: '',
      TRAVEL_ROOMS: '1',
      TRAVEL_WITH_BAGGAGE: '',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: details?.searchId ?? '',
      TRAVEL_PRODUCT_CODE: details?.hotelCode ?? '',
      TRAVEL_PRODUCT_NAME: details?.hotelName ?? '',
      TRAVEL_SUB_PRODUCT: '',
      TRAVEL_PRODUCT_CATEGORY: details?.category?.toString() ?? '',
      TRAVEL_PRODUCT_PRICE: details
        ? (details.rooms.first().roomRate?.price?.total / daysCount(checkinDate, checkoutDate)).toString()
        : '0',
      TRAVEL_PRODUCT_PROVIDER: details?.hotelProvider ?? '',
      TRAVEL_FARE_TYPE: '',
    };

    amplitudelogEvent(amplitudeEvents.ADD_TO_CART, amplitudeEvent, accountResult);
  };

  const handleConfirm = (room: Room, roomIndex: number) => {
    if (formik && name) {
      formik.setFieldValue(name, {
        roomRateId: room.roomRate.rateId,
        roomRate: { refundableType: room.roomRate.refundableType },
        breakRulesMessage: room.roomRate.price.breakRulesMessage,
      });
      formik.setFieldTouched(name);
      visibility();

      sendAmplitudeEvent(room, roomIndex);
    }
  };

  const amountDays = useMemo(() => {
    const iDate = moment(checkinDate, 'YYYY-MM-DD');
    const eDate = moment(checkoutDate, 'YYYY-MM-DD');
    return eDate.diff(iDate, 'days');
  }, [checkinDate, checkoutDate]);

  const renderRoomRate = (room: Room) => {
    switch (room.roomRate.refundableType) {
      case null:
        return null;
      case 'refundable':
        return (
          <ContentRefundableItems>
            <RefundableIcon />
            <TextIsRefundable>{t('hotelDetails.roomRefundable')}</TextIsRefundable>
          </ContentRefundableItems>
        );
      case 'nonrefundable':
        return (
          <TextIsNotRefundable>
            <NonRefundableIcon />
            {t('hotelDetails.roomNonRefundable')}
          </TextIsNotRefundable>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {rooms.map((room, roomIndex) => {
        const rateId = room.roomRate.rateId;
        const { breakRulesMessage } = room.roomRate.price;
        const perNightPrice = (room.roomRate.price.total / amountDays).toFixed(2);

        return (
          <Container key={room.roomId}>
            <RoomTitle>{room.description}</RoomTitle>
            {room.roomRate.textFare && <Fare>{room.roomRate.textFare}</Fare>}
            {room.boardDescription && <Description>{room.boardDescription}</Description>}
            {renderRoomRate(room)}
            <Footer>
              <div>
                <PerNightValue>
                  {t('hotelsCard.nightPrice', { value: formattedPrice(Number(perNightPrice)) })}
                </PerNightValue>
                <Value>{formattedPrice(room.roomRate.price.total)}</Value>
                {roomIndex === ROOM_INDEX_BEST_OPTION && !breakRulesMessage?.length ? (
                  <Tag>
                    <BestOptionLabel>{t('hotelDetails.bestOption')}</BestOptionLabel>
                  </Tag>
                ) : (
                  breakRulesMessage?.length > 0 && (
                    <Tag>
                      <BreakTag reason={breakRulesMessage} />
                    </Tag>
                  )
                )}
              </div>
              {!bookingDisabled && (
                <Selection type="button" onClick={() => handleConfirm(room, roomIndex)}>
                  {t('hotelDetails.selectRoomAction')}
                </Selection>
              )}
            </Footer>

            <CancellationPolicies
              data={policies[rateId]}
              show={showPolicies[rateId]}
              isFetching={isFetchingPolicies[rateId]}
              onClick={() => handleTogglePolicies(rateId)}
            />
          </Container>
        );
      })}
    </>
  );
};

export default Rooms;
